import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { fetchFareQuote, fetchFareRules, FallbackAvailableError } from "../services/flightBookingAPI";
import BookingReviewError from "../components/bookingFlight/BookingReviewError";
import LoadingModal from "../components/modal/LoadingModal";
import FallbackErrorModal from "../components/modal/FallbackErrorModal";
import BookingReview from "../components/bookingFlight/BookingReview";
import TravellerDetails from "../components/bookingFlight/TravellerDetails";
import FareRules from "../components/bookingFlight/FareRules";
import FareDetails from "../components/bookingFlight/FareDetails";
import PromoCodeList from "../components/bookingFlight/PromoCodeList";
import PaymentMethod from "../components/bookingFlight/PaymentMethod";
import BookingConfirmation from "../components/bookingFlight/BookingConfirmation";
import ContactSupportModal from "../components/bookingFlight/ContactSupportModal";
import GSTBillingDetails from "../components/bookingFlight/GSTBillingDetails";
import { useAuth } from "../context/AuthContext";
import { getAirlineLogo } from "../utils/airlineImages";
import {
  RecaptchaVerifier
} from "firebase/auth";
import { useBookingLeadSignals } from "../components/lead-bot";

// Trip context for the lead bot, read defensively from the TBO fare quote
const flightLeadContext = (fq, rcBookingId, phone) => {
  const res = fq?.Results || {};
  let segs = res.Segments || [];
  if (Array.isArray(segs[0])) segs = segs[0];
  const first = segs[0] || {};
  const last = segs[segs.length - 1] || first;
  const o = first.Origin?.Airport || {};
  const d = last.Destination?.Airport || {};
  return {
    mode: "flight",
    src_name: o.CityName || o.AirportName || "",
    src_code: o.AirportCode || "",
    dest_name: d.CityName || d.AirportName || "",
    dest_code: d.AirportCode || "",
    travel_date: String(first.Origin?.DepTime || "").slice(0, 10),
    amount: Number(fq?.final_total_fare_with_taxes || res.Fare?.PublishedFare || 0) || null,
    rcBookingId: rcBookingId || null,
    prefillPhone: phone || "",
  };
};

const BookFlight = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract parameters from URL first, then fallback to location state
  const searchParams = new URLSearchParams(location.search);
  const trace_id = searchParams.get("trace_id") || location.state?.trace_id;
  const result_idx =
    searchParams.get("result_idx") || location.state?.result_idx;
  // eslint-disable-next-line no-unused-vars
  const is_lcc = searchParams.get("is_lcc") || location.state?.is_lcc; // Stored for potential future use
  // eslint-disable-next-line no-unused-vars
  const valid_till_ts = searchParams.get("valid_till_ts") || location.state?.valid_till_ts; // Stored for potential future use

  // Utility to decode URL-safe Base64 'fb' parameter
  const decodeFbParam = (fbValue) => {
    if (!fbValue) return null;
    try {
      let base64 = fbValue.replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) {
        base64 += '=';
      }
      return atob(base64);
    } catch (e) {
      console.error("Error decoding fb param:", e);
      return null;
    }
  };

  const fbUrl = decodeFbParam(searchParams.get("fb"));
  // console.log('fbUrl', fbUrl)
  const source = location.state?.source || localStorage.getItem("booking_source") || "chat";
  const searchQuery = location.state?.searchQuery || localStorage.getItem("booking_search_query") || "";

  // Get userToken from localStorage or location state
  const [userToken, setUserToken] = useState(
    localStorage.getItem("userToken") ||
    location.state?.userToken ||
    ""
  );

  // API Data States
  const [fareQuoteData, setFareQuoteData] = useState(null);
  const [fareRulesData, setFareRulesData] = useState(null);

  // UI States
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const [currentPage, setCurrentPage] = useState("booking"); // 'booking', 'payment', or 'confirmation'
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isTransactionActive, setIsTransactionActive] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);

  // 3-Minute Precise Refresh Logic: Triggers a full page reload every 180 seconds 
  // to keep fares fresh, while tracking elapsed time across manual reloads.
  useEffect(() => {
    // Basic guards: don't start timer if IDs are missing
    if (!trace_id || !result_idx) return;

    // Safety: Skip refresh if transaction is active or user is beyond the booking review step
    if (isTransactionActive || currentPage !== "booking") {
      // If booking is confirmed, clean up the session target for this flight
      if (currentPage === "confirmation") {
        sessionStorage.removeItem(`rf_target_${trace_id}_${result_idx}`);
      }
      return;
    }

    const refreshKey = `rf_target_${trace_id}_${result_idx}`;
    const REFRESH_DURATION = 3 * 60 * 1000; // 3 minutes in ms

    // Logic to identify when the next refresh should happen
    const getTargetTime = () => {
      const saved = sessionStorage.getItem(refreshKey);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) return parsed;
      }
      // If no target set, create one for 3 mins from now
      const newTarget = Date.now() + REFRESH_DURATION;
      sessionStorage.setItem(refreshKey, newTarget.toString());
      return newTarget;
    };

    const targetTime = getTargetTime();
    let timer;

    // Logic to check expiration and reload only if the user is active on the page
    const checkAndReload = () => {
      if (!document.hidden && Date.now() >= targetTime) {
        clearInterval(timer);
        document.removeEventListener("visibilitychange", checkAndReload);
        sessionStorage.removeItem(refreshKey);
        window.location.reload();
      }
    };

    // Check every second to see if target has been reached
    timer = setInterval(checkAndReload, 1000);

    // Also trigger immediately when the user switches back to this tab
    document.addEventListener("visibilitychange", checkAndReload);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", checkAndReload);
    };
  }, [isTransactionActive, currentPage, trace_id, result_idx]);

  const { user, userToken: globalToken, openLoginModal } = useAuth();

  useEffect(() => {
    if (globalToken) {
      setUserToken(globalToken);
    }
  }, [globalToken]);

  const isFetchedRef = useRef(false);

  // Fallback Error Modal State
  const [fallbackError, setFallbackError] = useState(null);

  // User Input State - Pre-populate from Firebase Auth
  const [contactDetails, setContactDetails] = useState({
    email: "",
    phone: "",
    countryCode: "+91",
  });

  // Passenger data state for TBO API
  const [passengers, setPassengers] = useState([]);

  // Billing address and GST details
  const [billingDetails, setBillingDetails] = useState(null);

  const handleBillingDetailsChange = (details) => {
    setBillingDetails(details);
  };

  // Get user email/phone from Firebase Auth on mount
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      // console.log("[BookFlight] Firebase user found:", user.email, user.phoneNumber);
      user.getIdToken().then(token => {
        setUserToken(token);
      });
      setContactDetails(prev => ({
        ...prev,
        email: user.email || prev.email,
        phone: user.phoneNumber || prev.phone,
      }));
    } else {
      // console.log("[BookFlight] No Firebase user found, listening for auth state...");
      // Listen for auth state changes in case user isn't loaded yet
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          // console.log("[BookFlight] Firebase auth state changed:", user.email, user.phoneNumber);
          const token = await user.getIdToken();
          setUserToken(token);
          setContactDetails(prev => ({
            ...prev,
            email: user.email || prev.email,
            phone: user.phoneNumber || prev.phone,
          }));
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // Debug: Log contact details changes
  // useEffect(() => {
  //   console.log("BookFlight contactDetails updated:", contactDetails);
  // }, [contactDetails]);

  const isInitialMount = useRef(true);
  const [authWaitOver, setAuthWaitOver] = useState(false);
  const hasAutoOpenedModal = useRef(false);

  // Give Firebase Auth 1.5 seconds to settle before we decide there is no token
  useEffect(() => {
    const timer = setTimeout(() => {
      setAuthWaitOver(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Auto-open login modal removed per user request - only open on 401

  // Fetch booking data on mount - controlled by authWaitOver or existence of token
  useEffect(() => {
    const fetchBookingData = async () => {
      // Validate required parameters
      if (!trace_id || result_idx === undefined || result_idx === null) {
        console.error("Missing required booking parameters:", { trace_id, result_idx });
        setIsLoading(false);
        setIsContactModalOpen(true);
        return;
      }

      // If we already have data or are already fetching, don't do it again
      if (isFetchedRef.current) return;

      try {
        // setIsLoading(true); // Restart loader for fetch attempt
        setApiError(null);

        // Determine effective token
        let effectiveToken = userToken;
        if (!effectiveToken) {
          const user = auth.currentUser;
          if (user) effectiveToken = await user.getIdToken();
        }

        // Proceed with fetch even without token to allow server to decide (401 flow)

        // Mark as fetched only when we have a token and are actually calling the API
        isFetchedRef.current = true;

        let fareQuoteResponse = null;

        // 1. Check if we have pre-fetched data from the search result card
        if (location.state?.preFetchedQuote) {
          fareQuoteResponse = location.state.preFetchedQuote;
          // IMPORTANT: Clear the state after use to ensure a reload DOES trigger a fresh fetch
          if (window.history.replaceState) {
            window.history.replaceState({ ...(location.state || {}), preFetchedQuote: null }, document.title);
          }
        } else {
          // 2. Fetch fareQuote if No data or on RELOAD
          fareQuoteResponse = await fetchFareQuote(trace_id, result_idx, effectiveToken);
        }

        if (!fareQuoteResponse || fareQuoteResponse.status !== 200) {
          throw new Error(fareQuoteResponse?.message || "Failed to fetch fare quote");
        }

        if (!fareQuoteResponse.data?.Results) {
          throw new Error("Invalid fare quote data structure");
        }

        const rc_booking_id = fareQuoteResponse.rc_booking_id;

        // Now fetch fareRules (Always fetch rules as they might be dynamic/fresh)
        const fareRulesResponse = await fetchFareRules(trace_id, result_idx, effectiveToken, rc_booking_id);

        if (!fareRulesResponse || fareRulesResponse.status !== 200) {
          throw new Error(fareRulesResponse?.message || "Failed to fetch fare rules");
        }

        // Success - store data
        setFareQuoteData(fareQuoteResponse.data);
        setFareRulesData({
          ...fareRulesResponse.data,
          rc_booking_id: fareRulesResponse.rc_booking_id || rc_booking_id
        });

        // Initialize applied promo from default
        const defaultPromo = fareQuoteResponse.data?.flat_off_flight_promo_default;
        const defaultAmount = fareQuoteResponse.data?.flat_off_amount_flight_default;
        if (defaultPromo && defaultAmount) {
          const defaultDesc = fareQuoteResponse.data?.valid_promo_codes?.[defaultPromo]?.desc || "";
          setAppliedPromo({ code: defaultPromo, amount: defaultAmount, description: defaultDesc });
        } else {
          setAppliedPromo({ code: '', amount: 0, description: '' });
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching booking data:", error);
        isFetchedRef.current = false; // Allow retry on error

        const isAuthError =
          error.status === 401 ||
          error.response?.status === 401 ||
          error.message?.toLowerCase().includes("unauthorized") ||
          error.message?.toLowerCase().includes("login required") ||
          error.message?.toLowerCase().includes("auth verifier") ||
          error.message?.toLowerCase().includes("authentication required");

        if (isAuthError) {
          setApiError("Authentication required. Please sign in to continue with your booking.");
          openLoginModal();
          setIsLoading(false);
        } else if (error instanceof FallbackAvailableError) {
          setFallbackError(error);
          setIsLoading(false);
        } else {
          setApiError(error.message || "Failed to load booking details. Please try again.");
          setIsLoading(false);
        }
      }
    };

    // Trigger fetch only if we have a token OR we've waited long enough for one
    if (userToken || authWaitOver) {
      fetchBookingData();
    }
  }, [trace_id, result_idx, userToken, authWaitOver, navigate, location]);

  // Google Ads Conversion tracking helper
  const reportConversion = (url) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-11440938612/A2c_CPfPh4scEPS8u88q',
        'value': 1.0,
        'currency': 'INR',
        'transaction_id': '',
        'event_callback': () => {
          if (url) window.location = url;
        }
      });
    }
  };

  const handleRetry = () => {
    // console.log("Retrying data fetch...");
    window.location.reload();
  };

  // State for payment data from create_order
  const [paymentData, setPaymentData] = useState(null);

  const handleProceedToPayment = (orderData) => {
    //console.log("[BookFlight] Proceeding to payment with order data:", orderData);
    setPaymentData(orderData);
    setCurrentPage("payment");
  };

  const handlePaymentSuccess = () => {
    setCurrentPage("confirmation");
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  useEffect(() => {
    const handleOpenContactSupport = () => {
      openContactModal();
    };

    window.addEventListener('open-contact-support', handleOpenContactSupport);

    return () => {
      window.removeEventListener('open-contact-support', handleOpenContactSupport);
    };
  }, []);

  // Fallback Modal Handlers
  const handleContinueWithMock = () => {
    //console.log("[BookFlight] User chose to continue with mock data");
    if (fallbackError && fallbackError.fallbackData) {
      // Determine which API failed and set the appropriate data
      if (fallbackError.operationName === "Fetch Fare Quote") {
        setFareQuoteData(fallbackError.fallbackData.data);
        // If we have fare quote but still need fare rules, fetch it
        if (!fareRulesData) {
          fetchFareRules(trace_id, result_idx, userToken)
            .then((response) => {
              setFareRulesData({
                ...response.data,
                rc_booking_id: response.rc_booking_id
              });
            })
            .catch((err) => {
              if (err instanceof FallbackAvailableError) {
                setFareRulesData({
                  ...err.fallbackData.data,
                  rc_booking_id: err.fallbackData.rc_booking_id
                });
              } else {
                // Check for 401 or Auth errors
                const isAuthError =
                  err.status === 401 ||
                  err.response?.status === 401 ||
                  err.message?.toLowerCase().includes("auth verifier") ||
                  err.message?.toLowerCase().includes("authentication required");

                if (isAuthError) {
                  openLoginModal();
                }
              }
            });
        }
      } else if (fallbackError.operationName === "Fetch Fare Rules") {
        setFareRulesData({
          ...fallbackError.fallbackData.data,
          rc_booking_id: fallbackError.fallbackData.rc_booking_id
        });
        // If we have fare rules but still need fare quote, fetch it
        if (!fareQuoteData) {
          fetchFareQuote(trace_id, result_idx, userToken)
            .then((response) => {
              setFareQuoteData(response.data);
            })
            .catch((err) => {
              if (err instanceof FallbackAvailableError) {
                setFareQuoteData(err.fallbackData.data);
              } else {
                // Check for 401 or Auth errors
                const isAuthError =
                  err.status === 401 ||
                  err.response?.status === 401 ||
                  err.message?.toLowerCase().includes("auth verifier") ||
                  err.message?.toLowerCase().includes("authentication required");

                if (isAuthError) {
                  openLoginModal();
                }
              }
            });
        }
      }
    }
    setFallbackError(null);
  };

  const handleTryAgainFallback = () => {
    //console.log("[BookFlight] User chose to try again");
    setFallbackError(null);
    setIsLoading(true);
    window.location.reload();
  };

  const handleGoBackFallback = () => {
    //console.log("[BookFlight] User chose to go back");
    setFallbackError(null);
    if (source === "search") {
      navigate(`/search-form${searchQuery || ""}`);
    } else {
      navigate("/chat");
    }
  };

  // Intercept back button and refresh when transaction is active
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isTransactionActive) {
        e.preventDefault();
        e.returnValue = 'Booking is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (isTransactionActive) {
        const confirmLeave = window.confirm('A booking is in progress. If you go back, your current progress will be lost. Are you sure you want to cancel the booking process?');

        if (confirmLeave) {
          // If user confirms, we stop the transaction block. 
          // History already moved back to the 'real' URL when back was pressed.
          setIsTransactionActive(false);
        } else {
          // If they cancel leave, push the dummy state back to keep intercepting
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    if (isTransactionActive) {
      // Add a dummy state to history to enable catching the back button
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isTransactionActive, navigate]);

  // ── Lead bot (Routy) ─────────────────────────────────────────────────────
  // Payment started then stopped while still on the review page = closed / abandoned payment
  const [paymentDismissed, setPaymentDismissed] = useState(false);
  const prevTxnRef = useRef(false);
  useEffect(() => {
    if (isTransactionActive) setPaymentDismissed(false);
    else if (prevTxnRef.current && currentPage === "booking" && !apiError) setPaymentDismissed(true);
    prevTxnRef.current = isTransactionActive;
  }, [isTransactionActive, currentPage, apiError]);

  const isAuthApiError = !!apiError && /auth|sign in|login/i.test(apiError);
  const leadStage = apiError ? "error" : currentPage === "booking" ? "review" : currentPage;
  const rcIdForLead = fareRulesData?.rc_booking_id || null;
  useBookingLeadSignals({
    booking: {
      ...flightLeadContext(fareQuoteData, rcIdForLead, contactDetails.phone),
      stage: leadStage,
      key: `flight:${rcIdForLead || `${trace_id}-${result_idx}`}`,
    },
    stage: leadStage,
    bookError: !!apiError && !isAuthApiError,
    paymentDismissed,
    suppress: {
      loading: isLoading,
      transaction: isTransactionActive,
      fallback: !!fallbackError,
      contactModal: isContactModalOpen,
      authError: isAuthApiError,
      confirmed: currentPage === "confirmation",
    },
    guardBack: true,
  });


  return (
    <div className="min-h-screen bg-gray-50 mt-16">

      {/* Loading State */}
      <LoadingModal
        isOpen={isLoading}
        title="Loading Your Booking Details"
        message="This will only take a moment. Thank you for your patience."
        showWarning={true}
        warningText="Please do not refresh this page or use the back button while we load your booking details."
      />

      {/* Fallback Error Modal */}
      <FallbackErrorModal
        isOpen={!!fallbackError}
        operationName={fallbackError?.operationName || ""}
        errorMessage={fallbackError?.message || ""}
        errorDetails={fallbackError?.errorDetails}
        onContinueWithMock={handleContinueWithMock}
        onTryAgain={handleTryAgainFallback}
        onGoBack={handleGoBackFallback}
      />

      {/* Error State */}
      {!isLoading && apiError && (
        <BookingReviewError
          errorMessage={apiError}
          onRetry={handleRetry}
          source={source}
          searchQuery={searchQuery}
          fbUrl={fbUrl}
          onSignIn={
            (apiError.toLowerCase().includes("authentication") ||
              apiError.toLowerCase().includes("sign in") ||
              apiError.toLowerCase().includes("login") ||
              apiError.toLowerCase().includes("auth"))
              ? () => openLoginModal()
              : null
          }
        />
      )}

      {/* Success State - Show Booking UI */}
      {!isLoading && !apiError && fareQuoteData && fareRulesData && (() => {
        const getFinalPaymentAmount = () => {
          let baseAmount = fareQuoteData?.final_total_fare_with_taxes || fareRulesData?.data?.Results?.Fare?.PublishedFare || fareQuoteData?.Results?.Fare?.PublishedFare;
          const discount = appliedPromo?.amount || 0;
          return Math.max(0, baseAmount - discount);
        };

        return (
          <div className="max-w-[1440px] mx-auto p-4 sm:p-6 lg:p-8">
            {currentPage === "booking" ? (
              <div className="flex flex-col-reverse lg:flex-row gap-6">
                {/* Main Content */}
                <div className="flex-1 space-y-6 w-full lg:max-w-[calc(100%-384px)]">
                  <BookingReview
                    onContactSupport={openContactModal}
                    fareQuoteData={fareQuoteData}
                    fareRulesData={fareRulesData}
                  />

                  <TravellerDetails
                    fareQuoteData={fareQuoteData}
                    trace_id={trace_id}
                    result_idx={result_idx}
                    contactDetails={contactDetails}
                    setContactDetails={setContactDetails}
                    passengers={passengers}
                    setPassengers={setPassengers}
                  />
                  <GSTBillingDetails
                    onChange={handleBillingDetailsChange}
                    isGstMandatory={fareQuoteData?.Results?.IsGSTMandatory || false}
                  />
                  <FareRules
                    fareRulesData={fareRulesData}
                    fareQuoteData={fareQuoteData}
                    onProceedToPayment={handleProceedToPayment}
                    trace_id={trace_id}
                    result_idx={result_idx}
                    userToken={userToken}
                    contactDetails={contactDetails}
                    passengers={passengers}
                    billingDetails={billingDetails}
                    rc_booking_id={fareRulesData?.rc_booking_id}
                    amount={fareQuoteData?.final_total_fare_with_taxes || fareRulesData?.data?.Results?.Fare?.PublishedFare || fareQuoteData?.Results?.Fare?.PublishedFare}
                    is_lcc={is_lcc === "true" || is_lcc === true}
                    isTransactionActive={isTransactionActive}
                    onTransactionStateChange={setIsTransactionActive}
                    reportConversion={reportConversion}
                    source={location.state?.source || "chat"}
                    searchQuery={location.state?.searchQuery || ""}
                    onAuthError={(msg) => setApiError(msg)}
                    appliedPromoCode={appliedPromo?.code || ""}
                  />
                </div>

                {/* Sidebar */}
                <div className="w-full lg:w-[360px] flex-shrink-0 lg:sticky lg:top-20 self-start">
                  <FareDetails fareQuoteData={fareQuoteData} appliedPromo={appliedPromo} />
                  <PromoCodeList
                    fareQuoteData={fareQuoteData}
                    userToken={userToken}
                    rcBookingId={fareRulesData?.rc_booking_id}
                    appliedPromo={appliedPromo}
                    setAppliedPromo={setAppliedPromo}
                  />
                </div>
              </div>
            ) : (
              <BookingConfirmation
                onContactSupport={openContactModal}
                fareQuoteData={fareQuoteData}
              />
            )}
          </div>
        )
      })()}

      {/* Contact Support Modal */}
      <ContactSupportModal
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
      />

      {/* recaptcha-container will be created by useAuthHandlers if needed */}
    </div>
  );
};

export default BookFlight;